import React from 'react';
import { useInView } from 'react-intersection-observer';
import {
  IconAI,
  IconChatbot,
  IconCustomerService,
  IconVision,
  IconVoice,
  IconKnowledgeBase,
  IconTranslation,
  IconTraining,
  IconInfrastructure,
  IconServer,
  IconMaintenance,
  IconConsultant
} from './icons/IconPack';

interface ProductCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
}

const ProductCard: React.FC<ProductCardProps> = ({ icon, title, description, delay }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <div 
      ref={ref}
      className={`card-professional bg-white p-8 rounded-lg transition-standard ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="icon-container primary mb-6">{icon}</div>
      <h3 className="text-heading-md text-blue-800 mb-4">{title}</h3>
      <p className="text-body-md text-gray-600 leading-relaxed">{description}</p>
    </div>
  );
};

const SubProductCard: React.FC<ProductCardProps> = ({ icon, title, description, delay }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <div 
      ref={ref}
      className={`bg-white p-6 rounded-lg border border-gray-100 transition-standard hover:border-blue-200 ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="icon-container mb-4">{icon}</div>
      <h4 className="text-heading-sm text-blue-800 mb-2">{title}</h4>
      <p className="text-body-sm text-gray-600 leading-relaxed">{description}</p>
    </div>
  );
};

interface ProductsProps {
  language: string;
}

const Products: React.FC<ProductsProps> = ({ language }) => {
  return (
    <section id="products" className="py-20 px-4 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-heading-lg text-center mb-12 section-heading-underline centered">
          {language === 'zh' ? '我們的解決方案' : 'Our Solutions'}
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          <div className="col-span-1 lg:col-span-3">
            <ProductCard 
              icon={<IconAI />}
              title={language === 'zh' ? 'AI 應用開發' : 'AI Application Development'}
              description={language === 'zh' ? '精心定制的 AI 解決方案，專為解決您的特定業務挑戰和機遇而設計。' : 'Custom AI solutions meticulously tailored to address your specific business challenges and opportunities.'}
              delay={0}
            />
            
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <SubProductCard
                icon={<IconChatbot />}
                title={language === 'zh' ? '問答系統解決方案' : 'Chatbot Solutions'}
                description={
                  language === 'zh'
                    ? '基於開源模型，量身打造問答系統，強化 RAG，提供精準且具情境感知的答案，全面提升客戶互動體驗。'
                    : 'Q&A chatbot solutions built on open-source models, tailor-made for your requirements, featuring enhanced Retrieval-Augmented Generation (RAG) for accurate, context-aware answers and superior customer engagement.'
                }
                delay={100}
              />
              
              <SubProductCard
                icon={<IconCustomerService />}
                title={language === 'zh' ? '多語客服系統' : 'Multilingual Customer Service'}
                description={
                  language === 'zh'
                    ? '先進智能客服系統，支援文字與語音對話，涵蓋 10 大主流及亞洲語言。系統結合商業知識與優化流程微調模型，確保精準且具情境感知的互動。強化 RAG 架構，實現高效本地數據存取，為企業量身打造準確且具文化脈絡的回應。'
                    : 'Advanced intelligent support systems designed for seamless communication across text-based and voice-based conversations, supporting 10 major and Asian languages. These systems leverage a fine-tuned model enriched with business knowledge and optimized work processes, ensuring precise and context-aware interactions. Additionally, an enhanced Retrieval-Augmented Generation (RAG) framework enables efficient local data access, delivering accurate and culturally relevant responses tailored to your organization\'s needs.'
                }
                delay={200}
              />
              
              <SubProductCard
                icon={<IconVision />}
                title={language === 'zh' ? '電腦視覺系統' : 'Computer Vision Systems'}
                description={
                  language === 'zh'
                    ? '先進的圖像識別與內容生成技術，專為多樣化應用場景量身定制，包括監控、手勢檢測、文件管理和交通控制。這些精密系統支援即時分析與自動化，提升安全性、用戶互動、運營效率和城市交通管理。'
                    : 'Advanced image recognition and content generation technologies tailored for diverse applications, including surveillance monitoring, gesture detection, document management, and traffic control. These sophisticated systems enable real-time analysis and automation, enhancing security, user interaction, operational efficiency, and urban mobility management.'
                }
                delay={300}
              />
              
              <SubProductCard
                icon={<IconVoice />}
                title={language === 'zh' ? 'AI 智能客服中心' : 'AI Call Center'}
                description={
                  language === 'zh'
                    ? '整合式 AI 驅動電話系統，實現無縫接聽與外撥通話。\n\n智能助理與秘書：自動排程、訊息處理與後續追蹤\nVoIP 整合：無縫接入現有網路，提供清晰低延遲通話\n自動化任務：預約、確認與 CRM 更新，無需人工轉接\n多語言支援：即時語言切換\n精調模型：客製化 AI 代理，優化語音轉文字與文字轉語音，實現自然準確對話'
                    : 'A unified, AI-powered phone system for seamless inbound/outbound calls.\n\nSmart Assistant & Secretary: Schedules, messages, and follows up automatically\nVoIP Integration: Plug into your existing network for clear, low-latency voice\nAutomated Tasks: Bookings, confirmations, and CRM updates—no human handoff\nMultilingual Support: On-the-fly language switching\nFine-Tuned Models: Custom AI agents plus optimized STT and TTS for natural, accurate conversations'
                }
                delay={400}
              />
              
              <SubProductCard
                icon={<IconKnowledgeBase />}
                title={language === 'zh' ? 'AI 驅動的知識管理' : 'AI-Powered Knowledge Management'}
                description={
                  language === 'zh'
                    ? '智能系統專為組織、檢索和利用組織知識資產而設計，並增強了自動執行指定任務的功能。這些系統支援多語言輸入與輸出，並接受多種輸入格式，包括文字、語音和圖像。通過嵌入式檢索增強生成（RAG）框架實現精準資訊搜索，同時利用 GraphRAG 進行全面的全球分析，確保提供可操作的洞察和高效的知識管理，滿足您組織的需求。'
                    : 'Intelligent systems designed for organizing, retrieving, and utilizing organizational knowledge assets, enhanced with automated execution of assigned tasks. These systems support multiple languages for both input and output, and accept diverse input formats including text, voice, and images. Leveraging an embedded Retrieval-Augmented Generation (RAG) framework, they enable precise information search, while utilizing GraphRAG for comprehensive global analysis, ensuring actionable insights and efficient knowledge management across your organization.'
                }
                delay={500}
              />
              
              <SubProductCard
                icon={<IconTranslation />}
                title={language === 'zh' ? '特定領域翻譯' : 'Domain-Specific Translation'}
                description={
                  language === 'zh'
                    ? '專為專業商業領域（包括醫療、金融科技、技術和文化產業）量身定制的高級翻譯服務，精準適應專業術語和語境。我們的解決方案支援特定的語言對，並提供整合的配音和語音克隆功能，實現自然的多語言音頻輸出。此外，我們還為媒體提供轉錄和字幕創建服務，確保跨文字、音頻和視頻格式的無縫溝通。'
                    : 'Advanced translation services tailored for professional business sectors—including Healthcare, FinTech, Technology, and Cultural industries—with precise adaptation to specialized terminology and contexts. Our solutions support specific language pairs and offer integrated voice-over and voice cloning capabilities, enabling natural-sounding multilingual audio output. Additionally, we provide transcription and subtitle creation for media, ensuring seamless communication across text, audio, and video formats.'
                }
                delay={600}
              />
            </div>
          </div>
          
          <div className="col-span-1">
            <ProductCard 
              icon={<IconInfrastructure />}
              title={language === 'zh' ? 'AI 基礎設施' : 'AI Infrastructure'}
              description={
                language === 'zh'
                  ? `利用我們量身定制的 AI 基礎設施服務打造高效能 AI 平台：  \n\nHPC AI 系統設計：設計可擴展叢集，支援訓練與推理。  \nGPU 資源管理：集中調度，提升 GPU 使用率並降低成本。  \nMLOps 架構：建立 CI/CD 流水線與監控，優化機器學習流程。\n\n我們提供從規劃到部署的可擴展 AI 基礎，推動創新並提升投資回報。`
                  : "Use our tailored AI infrastructure services to build high-performance AI platforms:    HPC AI System Design - Design scalable clusters for training and inference.    GPU Resource Management - Centralized scheduling to boost GPU use and cut costs.    MLOps Architecture - Set up CI/CD pipelines and monitoring for machine learning.     We offer scalable AI foundations from planning to deployment, driving innovation and ROI."
              }
              delay={800}
            />
            
            <div className="mt-8 grid grid-cols-1 gap-6">
              <SubProductCard
                icon={<IconServer />}
                title={language === 'zh' ? 'AI 伺服器架構' : 'AI Server Architecture'}
                description={
                  language === 'zh'
                    ? '利用基於 NVIDIA 高性能工作站的客製化伺服器基礎設施，專為繁重模型訓練和極速推理優化：  \n專業部署：針對醫療系統、AI 驅動的金融交易等關鍵領域進行優化配置，確保合規性、超低延遲和領域特定可靠性。  \n大規模並行處理：擴展多 GPU 叢集以並行處理大量數據，適合金融流量的即時詐欺或可疑交易分析。  \n端到端性能：從 PCI-e 拓撲、NVLink 架構到高吞吐量儲存和網路結構，每一層都經過設計以最大化吞吐量並縮短訓練時間。'
                    : 'Leverage custom-engineered server infrastructure built on NVIDIA high-performance workstations, purpose-tuned for both heavy model training and lightning-fast inference: Specialized Deployment Optimized configurations for critical verticals—healthcare systems, AI-driven financial trading, and more—ensuring regulatory compliance, ultra-low latency, and domain-specific reliability. Massive Parallel Processing Scale out multi-GPU clusters to process vast datasets in parallel—ideal for real-time fraud or suspicious-transaction analysis on financial streams. End-to-End Performance From PCI-e topology and NVLink fabric to high-throughput storage and network fabrics, every layer is engineered to maximize throughput and minimize training times.'
                }
                delay={900}
              />
              
              <SubProductCard
                icon={<IconMaintenance />}
                title={language === 'zh' ? '基礎設施管理' : 'Infrastructure Management'}
                description={
                  language === 'zh'
                    ? '全面的支援服務，用於維護雲端和本地 AI 系統，增強了 GPU 資源共享以優化硬體使用效率。這些服務包括整合的會計與計費後端，簡化成本追蹤與分配，以及容器和模組化管理，實現無縫部署、可擴展性和系統模組化，確保高效且成本效益高的 AI 運營。'
                    : 'Comprehensive support services for maintaining both cloud-based and on-premises AI systems, enhanced with GPU resource sharing for optimized hardware utilization. These services include an integrated accounting and billing backend to streamline cost tracking and allocation, as well as container and modular management for seamless deployment, scalability, and system modularity, ensuring efficient and cost-effective AI operations.'
                }
                delay={1000}
              />
            </div>
          </div>
          
          <ProductCard 
            icon={<IconConsultant />}
            title={language === 'zh' ? 'AI 策略諮詢' : 'AI Strategic Consultation'}
            description={
              language === 'zh'
                ? `全面的 AI 顧問服務，協助您在策略、模型、基礎設施與運營上保持一致，以實現最大投資報酬率。

LLM 模型構建：基於您的專有數據，從零構建並微調定制的大型語言模型。

開發環境優化：精簡 CI/CD 流程、IDE 和 GPU 調度器，加快原型開發與部署速度。

機器學習基礎設施設計：構建可擴展、高性能的集群，搭配穩定的存儲與網路。

系統部署與冷卻：同步部署 AI 平台與先進的資料中心冷卻方案，以支撐高負載運行。

InfiniBand 網路：實施低延遲、高帶寬的網路架構，提升分散式訓練效率。

RDMA 加速：採用零拷貝、核心旁路傳輸，最大化 GPU 資源利用率。

攜手 DHT Solution，無縫整合 AI，驅動可量化的商業成效。`
                : `Comprehensive AI advisory to align your strategy, models, infrastructure, and operations for maximum ROI.

LLM Model Building: Build and fine-tune custom large-language models on your proprietary data.

Dev Environment Optimization: Streamline CI/CD pipelines, IDEs, and GPU schedulers for rapid prototyping and deployment.

ML Infrastructure Design: Architect scalable, high-performance clusters with robust storage and networking.

System Deployment & Cooling: Roll out AI platforms alongside advanced data-center cooling to sustain heavy workloads.

InfiniBand Networking: Deploy low-latency, high-bandwidth fabrics for efficient distributed training.

RDMA Acceleration: Use zero-copy, kernel-bypass transfers to maximize GPU utilization.

Partner with DHT Solution to seamlessly integrate AI and drive measurable business impact.`
            }
            delay={1100}
          />
          
          <ProductCard 
            icon={<IconTraining />}
            title={language === 'zh' ? '專業培訓' : 'Professional Training'}
            description={
              language === 'zh'
                ? `提升您組織的 AI 能力：  
AI 認知培訓：為領導者提供 AI 應用案例與商業影響洞察。  

AI 應用開發訓練營：工程師實操培訓，涵蓋模型設計到部署。  

數據與機器學習工作坊：實務實驗，聚焦數據準備、特徵工程與模型評估。  

敏捷 AI 開發：整合 CI/CD、衝刺規劃與跨功能實踐，應用於 AI 專案。`
                : `Accelerate AI proficiency across your organization:

AI Awareness Training: Equip leaders with AI use cases and business impact insights.

AI App Dev Bootcamp: Hands-on engineer training from model design through deployment.

Data & ML Workshop: Practical labs on data prep, feature engineering, and model evaluation.

Agile AI Development: Integrate CI/CD, sprint planning, and cross-functional practices for AI projects.`
            }
            delay={700}
          />
        </div>
      </div>
    </section>
  );
};

export default Products; 